import style from './payment-status.module.css'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Input, Tooltip } from '@missionlabs/smartagent-app-components'
import ProgressBox, { ProgressBoxStatus } from '../../components/ProgressBox'
import reset from '../../icons/reset.svg'
import { useTimer } from 'hooks/useTimer'
import useInterval from 'hooks/useInterval'
import { useAppContext } from 'hooks/useAppContext'
import { IPaymentStatus } from 'types'
import useSaveCardDetails from './useSaveCardDetails'
import useSendSMS from './useSendSMS'
import { ClipLoader } from 'react-spinners'
import classNames from 'classnames'
import { useSmartagent } from '../../hooks/useSmartagent'
import SmartAgent from '@missionlabs/smartagent-app-components/dist/smartagent'

type StatusDependencies = Record<keyof IPaymentStatus, (keyof IPaymentStatus)[]>
const statusDependencies: StatusDependencies = {
    CARD_NUMBER_ENTERED: [],
    CARD_NUMBER_ERROR: [],
    CARD_CVV_ERROR: [],
    CARD_EXPIRY_ENTERED: ['CARD_NUMBER_ENTERED'],
    CARD_EXPIRY_ERROR: [],
    CARD_CVV_ENTERED: ['CARD_NUMBER_ENTERED', 'CARD_EXPIRY_ENTERED'],
    SENT_TO_SPS: [
        'CARD_NUMBER_ENTERED',
        'CARD_EXPIRY_ENTERED',
        'CARD_CVV_ENTERED'
    ],
    COMPLETE: [
        'CARD_NUMBER_ENTERED',
        'CARD_EXPIRY_ENTERED',
        'CARD_CVV_ENTERED',
        'SENT_TO_SPS',
        'SENT_TO_POST_CASH'
    ],
    ERROR: [],
    INITIALIZED: [],
    READY: [],
    SENT_TO_POST_CASH: [
        'CARD_NUMBER_ENTERED',
        'CARD_EXPIRY_ENTERED',
        'CARD_CVV_ENTERED',
        'SENT_TO_SPS'
    ]
}

const PaymentStatus: React.FC<any> = (props) => {
    const sm = useRef<SmartAgent>(props.sm || new SmartAgent())
    const {
        paymentStatus,
        updatePaymentStatus,
        resetPaymentStatus,
        resetPaymentStatusAll,
        WPToken,
        mulesoftTicketId,
        updateAccountId,
        getMulesoftTicket,
        resetPluginsAll,
        userDetails,
        setSMSRecipient,
        contactPhoneNumber,
        setContactPhoneNumber,
        useDTMF
    } = useAppContext()
    const {
        saveCardLoading,
        saveCardDetails,
        saveCardError,
        saveCardDone,
        saveCardExistingToken
    } = useSaveCardDetails()
    const {
        sendSMS,
        sendSMSError,
        sendSMSDone,
        sendSMSLoading,
        phoneNumberError,
        setPhoneNumberError,
        validatePhoneNumber
    } = useSendSMS()
    const { contact, acqueonContactNumber } = useSmartagent()
    const timeElapsed = useTimer(0)
    const isCommercialAccountType = userDetails.type === 'Commercial'
    const [pciId, setPciId] = useState<string>('')
    const [mulesoftAttempts, setMulesoftAttempts] = useState(0)

    useInterval(() => {
        updatePaymentStatus()
        sm.current.sendMessage('get_current_contact')
    }, 2000)

    useInterval(
        () => {
            getMulesoftTicket()
            if (!mulesoftTicketId) {
                setMulesoftAttempts(mulesoftAttempts + 1)
            } else {
                setMulesoftAttempts(0)
            }
        },
        paymentStatus?.COMPLETE ? 2000 : null
    )

    useEffect(() => {
        if (acqueonContactNumber) {
            setContactPhoneNumber(acqueonContactNumber)
        } else if (contact?.customerEndpointAddress) {
            setContactPhoneNumber(contact.customerEndpointAddress)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    type Statuses = Record<keyof IPaymentStatus, ProgressBoxStatus>
    const statuses = useMemo<Statuses>(() => {
        const getStatus = (status: keyof IPaymentStatus): ProgressBoxStatus => {
            if (!paymentStatus) return 'default'
            if (
                (paymentStatus.COMPLETE && mulesoftTicketId) ||
                (status === 'SENT_TO_POST_CASH' &&
                    paymentStatus.SENT_TO_SPS &&
                    contact.attributes?.detailsStatus === '200' &&
                    contact.attributes?.detailsMessage === 'Y') ||
                (status !== 'COMPLETE' && paymentStatus[status])
            )
                return 'success'

            const dependenciesSatisfied = statusDependencies[status].reduce(
                (satisfied, status) => {
                    return !satisfied ? false : !!paymentStatus[status]
                },
                true
            )

            if (dependenciesSatisfied) {
                if (paymentStatus.ERROR) return 'failure'
                if (
                    status === 'CARD_NUMBER_ENTERED' &&
                    paymentStatus.CARD_NUMBER_ERROR
                )
                    return 'failure'
                if (
                    status === 'CARD_EXPIRY_ENTERED' &&
                    paymentStatus.CARD_EXPIRY_ERROR
                )
                    return 'failure'
                if (
                    status === 'CARD_CVV_ENTERED' &&
                    paymentStatus.CARD_CVV_ERROR
                )
                    return 'failure'
                if (
                    status === 'SENT_TO_POST_CASH' &&
                    (contact.attributes?.detailsStatus ||
                        contact.attributes?.detailsMessage) &&
                    (contact.attributes?.detailsStatus !== '200' ||
                        contact.attributes?.detailsMessage !== 'Y')
                ) {
                    return 'failure'
                }
                if (
                    status === 'COMPLETE' &&
                    mulesoftAttempts > 20 &&
                    !mulesoftTicketId
                )
                    return 'failure'
                return 'in_progress'
            }

            return 'default'
        }

        return {
            CARD_NUMBER_ENTERED: getStatus('CARD_NUMBER_ENTERED'),
            CARD_EXPIRY_ENTERED: getStatus('CARD_EXPIRY_ENTERED'),
            CARD_CVV_ENTERED: getStatus('CARD_CVV_ENTERED'),
            SENT_TO_SPS: getStatus('SENT_TO_SPS'),
            COMPLETE: getStatus('COMPLETE'),
            SENT_TO_POST_CASH: getStatus('SENT_TO_POST_CASH'),
            ERROR: 'default',
            INITIALIZED: 'default',
            CARD_EXPIRY_ERROR: 'default',
            CARD_NUMBER_ERROR: 'default',
            CARD_CVV_ERROR: 'default',
            READY: 'default'
        }
    }, [contact.attributes, mulesoftAttempts, mulesoftTicketId, paymentStatus])

    const showAfterPaymentDetails = () => {
        if (
            (paymentStatus?.COMPLETE &&
                statuses['COMPLETE'] !== 'in_progress') ||
            paymentStatus?.ERROR
        ) {
            return true
        } else {
            return false
        }
    }

    const canSendSms = () => {
        if (!paymentStatus?.SENT_TO_SPS) return false
        if (sendSMSLoading || sendSMSDone) return false
        if (!pciId && !mulesoftTicketId) return false

        return (
            statuses['SENT_TO_POST_CASH'] === 'success' ||
            statuses['SENT_TO_POST_CASH'] === 'failure'
        )
    }

    const setStyle = () => {
        if (paymentStatus?.ERROR) {
            return style.error
        } else if (showAfterPaymentDetails()) {
            return style.success
        } else return ''
    }

    return (
        <div className={style.wrapper}>
            <div className={style.row1}>
                <h2 className={style.header}>Capturing card details</h2>
                <Button
                    aria-label="reset payment"
                    type="button"
                    onClick={() => {
                        resetPaymentStatusAll()
                        updateAccountId('')
                        resetPluginsAll()
                        setSMSRecipient('')
                        setContactPhoneNumber('')
                    }}
                    className={style.resetButton}
                >
                    <span>Reset</span>
                    <img src={reset} alt="Reset" />
                </Button>
            </div>
            <div className={style.row2}>
                <ProgressBox
                    title="16 digit card number"
                    status={statuses['CARD_NUMBER_ENTERED']}
                    reset={
                        useDTMF
                            ? () =>
                                  resetPaymentStatus('CARD_NUMBER_ERROR', false)
                            : undefined
                    }
                />
                <ProgressBox
                    title="Expiry date"
                    status={statuses['CARD_EXPIRY_ENTERED']}
                    reset={
                        useDTMF
                            ? () =>
                                  resetPaymentStatus('CARD_EXPIRY_ERROR', false)
                            : undefined
                    }
                />
                <ProgressBox
                    title="CV2/CVV"
                    status={statuses['CARD_CVV_ENTERED']}
                    reset={
                        useDTMF
                            ? () => resetPaymentStatus('CARD_CVV_ERROR', false)
                            : undefined
                    }
                />
                <ProgressBox
                    title="Submitted to provider"
                    status={statuses['SENT_TO_SPS']}
                    reset={
                        useDTMF
                            ? () => resetPaymentStatus('SENT_TO_SPS', false)
                            : undefined
                    }
                />
                <ProgressBox
                    title="Payment processed"
                    status={statuses['SENT_TO_POST_CASH']}
                />
            </div>
            <div className={style.row3}>
                <div className={style['status-details']}>
                    {showAfterPaymentDetails() && (
                        <div className={style['status-details-item']}>
                            {mulesoftTicketId ? (
                                <>
                                    <div
                                        className={
                                            style['status-details-title']
                                        }
                                    >
                                        PCI ID*
                                    </div>
                                    <div className={setStyle()}>
                                        {mulesoftTicketId}
                                    </div>
                                </>
                            ) : (
                                <div className={style['pci-id-container']}>
                                    <div
                                        className={
                                            style['pci-id-label-container']
                                        }
                                    >
                                        <label htmlFor="pci-id">PCI ID*</label>
                                        <Tooltip
                                            className={style['default-tooltip']}
                                            text="You can find the PCI ID on the SAP C4C. Please copy and paste it here."
                                        >
                                            <aside
                                                className={style['info-icon']}
                                            >
                                                <span
                                                    className={
                                                        style['info-icon-text']
                                                    }
                                                >
                                                    i
                                                </span>
                                            </aside>
                                        </Tooltip>
                                    </div>
                                    <Input
                                        id="pci-id"
                                        required
                                        className={style['pci-id-input']}
                                        value={pciId}
                                        onChange={(value) => {
                                            setPciId(value as string)
                                            console.log('pciId: ', pciId)
                                            console.log('value: ', value)
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    <div className={`${style.timer} ${setStyle()}`}>
                        <label htmlFor="timer">Time Elapsed</label>
                        <time id="timer">{timeElapsed}</time>
                    </div>
                    {showAfterPaymentDetails() && (
                        <div className={style['status-details-item']}>
                            <div className={style['status-details-title']}>
                                Payment Reference Number
                            </div>
                            <div className={setStyle()}>
                                {' '}
                                {contact.attributes?.PlainPaymentRef}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className={style.row4}>
                <div className={style.smsCol}>
                    {showAfterPaymentDetails() && !isCommercialAccountType && (
                        <>
                            <div className={style.phoneNumber}>
                                <Input
                                    label="Phone Number"
                                    value={contactPhoneNumber}
                                    onChange={(customerPhoneNumber: any) => {
                                        setContactPhoneNumber(
                                            customerPhoneNumber
                                        )
                                        if (phoneNumberError)
                                            setPhoneNumberError('')
                                    }}
                                    onBlur={() =>
                                        validatePhoneNumber(contactPhoneNumber)
                                    }
                                />
                                <div
                                    className={`${style['input-error']} ${style['phone-input-error']}`}
                                >
                                    {' '}
                                    {(phoneNumberError !== '' ||
                                        sendSMSError) && (
                                        <>
                                            {phoneNumberError
                                                ? phoneNumberError
                                                : sendSMSError}
                                        </>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
                <div className={style.buttonsCol}>
                    <div className={style.buttonsWrapper}>
                        <div>
                            {showAfterPaymentDetails() &&
                                !isCommercialAccountType && (
                                    <Button
                                        className={classNames({
                                            [style.sendSMSButton]: true,
                                            [style.sendSMSError]: sendSMSError
                                        })}
                                        type="button"
                                        disabled={!canSendSms()}
                                        onClick={() => sendSMS(pciId)}
                                    >
                                        {sendSMSLoading ? (
                                            <ClipLoader
                                                size={24}
                                                color="#fff"
                                            />
                                        ) : (
                                            'Send SMS'
                                        )}
                                    </Button>
                                )}
                        </div>
                        <div>
                            {!isCommercialAccountType && (
                                <Button
                                    className={classNames({
                                        [style.saveTokenButton]: true,
                                        [style.saveTokenError]: saveCardError,
                                        [style.saveCardExistingToken]: saveCardExistingToken
                                    })}
                                    type="button"
                                    disabled={
                                        !paymentStatus?.SENT_TO_SPS ||
                                        saveCardLoading ||
                                        (saveCardDone &&
                                            !saveCardExistingToken) ||
                                        !props.tokenization ||
                                        !WPToken
                                    }
                                    onClick={saveCardDetails}
                                >
                                    {saveCardLoading ? (
                                        <ClipLoader size={24} color="#fff" />
                                    ) : (
                                        'Save Card Details'
                                    )}
                                </Button>
                            )}

                            <Button
                                className={style.confirmButton}
                                type="button"
                                disabled={!showAfterPaymentDetails()}
                                onClick={() => {
                                    resetPaymentStatusAll()
                                    updateAccountId('')
                                    setSMSRecipient('')
                                    setContactPhoneNumber('')
                                    sm.current.updateContact({
                                        'sa-pci-ticketid': mulesoftTicketId
                                            ? mulesoftTicketId
                                            : pciId
                                    })
                                }}
                            >
                                Confirm Completion
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PaymentStatus
